import React from "react";
import { ExternalLink, Globe, Twitter } from "lucide-react";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { CopyButton } from "./CopyButton";
import { shortAddr } from "../lib/utils";
import { cn } from "../lib/utils";

function Avatar({ src, symbol }) {
  if (src) {
    return (
      <img
        src={src}
        alt={symbol || "token"}
        loading="lazy"
        className="h-12 w-12 rounded-full bg-[hsl(var(--bg-elev-2))] border border-[hsl(var(--border))] object-cover"
      />
    );
  }
  return (
    <div className="h-12 w-12 rounded-full bg-[hsl(var(--bg-elev-2))] border border-[hsl(var(--border))] flex items-center justify-center text-xs font-mono text-[hsl(var(--muted))]">
      {symbol ? symbol.slice(0, 3).toUpperCase() : "—"}
    </div>
  );
}

function LinkChip({ href, children, testId }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      data-testid={testId}
      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--bg-elev-2))] text-xs text-[hsl(var(--muted))] hover:text-[hsl(var(--primary))] hover:border-[hsl(var(--primary)/0.4)] transition-colors"
    >
      {children}
      <ExternalLink size={11} />
    </a>
  );
}

export function TokenHeader({ report }) {
  const jp = report?.jupiter || {};
  const md = report?.metadata || {};
  const mint = report?.mint;
  const name = jp.name || md.name || "Unknown Token";
  const symbol = jp.symbol || md.symbol || "";
  const logo = jp.icon || "";
  const isVerified = jp.isVerified;
  const tags = Array.isArray(jp.tags) ? jp.tags.slice(0, 4) : [];
  return (
    <Card className="p-4 sm:p-5">
      <div className="flex items-start gap-4 flex-wrap">
        <Avatar src={logo} symbol={symbol} />
        <div className="flex-1 min-w-[220px]">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-xl sm:text-2xl font-semibold tracking-tight">
              {name}
            </h2>
            {symbol ? (
              <span className="text-sm font-mono text-[hsl(var(--muted))] uppercase">
                ${symbol}
              </span>
            ) : null}
            {isVerified ? (
              <Badge variant="primary">Verified</Badge>
            ) : null}
            {tags.map((t) => (
              <Badge key={t} variant="muted" className="text-[10px] tracking-wide">
                {t}
              </Badge>
            ))}
          </div>
          <div className="mt-2 flex items-center gap-2 text-xs font-mono text-[hsl(var(--muted))]">
            <span
              className="truncate"
              data-testid="token-header-mint"
              title={mint}
            >
              {mint}
            </span>
            <CopyButton
              value={mint}
              testId="token-header-mint-copy-button"
              label="Copy mint address"
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <LinkChip
            href={`https://solscan.io/token/${mint}`}
            testId="token-header-solscan-link"
          >
            Solscan
          </LinkChip>
          <LinkChip
            href={`https://dexscreener.com/solana/${mint}`}
            testId="token-header-dexscreener-link"
          >
            DexScreener
          </LinkChip>
          <LinkChip href={`https://birdeye.so/token/${mint}?chain=solana`}>
            Birdeye
          </LinkChip>
          <LinkChip href={`https://jup.ag/swap/SOL-${mint}`}>Jupiter</LinkChip>
          {jp.website ? (
            <LinkChip href={jp.website}>
              <Globe size={11} className="mr-1" /> Website
            </LinkChip>
          ) : null}
          {jp.twitter ? (
            <LinkChip href={jp.twitter}>
              <Twitter size={11} className="mr-1" /> Twitter
            </LinkChip>
          ) : null}
        </div>
      </div>
    </Card>
  );
}
